import React, { useState, useRef, useEffect, useCallback } from 'react';

/**
 * Компонент кастомного календаря с русской локализацией
 * Все даты обрабатываются по московскому времени (UTC+3)
 */
const CustomDatePicker = ({ value, onChange, label, className = '', name = '' }) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const calendarRef = useRef(null);
  
  // Русская локализация
  const monthNames = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
  const weekDays = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
  
  // Функция для получения даты в московском времени
  const getMoscowDate = (date) => {
    if (!date) return null;
    
    // Создаем новую дату с учетом московского часового пояса (UTC+3)
    const moscowDate = new Date(date);
    moscowDate.setHours(3, 0, 0, 0); // Устанавливаем 3 часа UTC, что соответствует 6 утра по Москве
    return moscowDate;
  };
  
  // Функция для безопасного парсинга даты
  const safeParseDate = useCallback((dateValue) => {
    if (!dateValue) return null;
    
    try {
      // Если это строка в формате ISO (YYYY-MM-DD)
      if (typeof dateValue === 'string') {
        // Сначала проверим ISO формат (YYYY-MM-DD)
        if (dateValue.includes('-')) {
          const parts = dateValue.split('T')[0].split('-');
          if (parts.length === 3) {
            const year = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1; // Месяцы в JS начинаются с 0
            const day = parseInt(parts[2], 10);
            
            // Создаем дату с учетом московского времени
            const date = new Date(Date.UTC(year, month, day, 3, 0, 0));
            return date;
          }
        }
        
        // Затем проверим российский формат (DD.MM.YYYY)
        if (dateValue.includes('.')) {
          const parts = dateValue.split('.');
          if (parts.length === 3) {
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1; // Месяцы в JS начинаются с 0
            const year = parseInt(parts[2], 10);
            
            // Создаем дату с учетом московского времени
            const date = new Date(Date.UTC(year, month, day, 3, 0, 0));
            return date;
          }
        }
      }
      
      // Если это объект Date
      if (dateValue instanceof Date && !isNaN(dateValue.getTime())) {
        // Создаем новую дату с учетом московского времени
        const moscowDate = new Date(dateValue);
        moscowDate.setHours(3, 0, 0, 0); // Устанавливаем 3 часа UTC
        return moscowDate;
      }
      
      // Пробуем общий конструктор Date
      const date = new Date(dateValue);
      if (!isNaN(date.getTime())) {
        const moscowDate = new Date(date);
        moscowDate.setHours(3, 0, 0, 0); // Устанавливаем 3 часа UTC
        return moscowDate;
      }
      
      // Если не удалось распарсить дату
      return null;
    } catch (e) {
      console.error('Ошибка парсинга даты:', e);
      return null;
    }
  }, []);
  
  // Форматирование даты в формат ДД.ММ.ГГГГ
  const formatDate = (date) => {
    if (!date) return '';
    // Получаем компоненты даты в московском времени
    const moscowDate = getMoscowDate(date);
    const day = moscowDate.getDate().toString().padStart(2, '0');
    const month = (moscowDate.getMonth() + 1).toString().padStart(2, '0');
    const year = moscowDate.getFullYear();
    return `${day}.${month}.${year}`;
  };
  
  // Форматирование даты в ISO формат (YYYY-MM-DD)
  const formatISODate = (date) => {
    if (!date) return '';
    
    // Получаем компоненты даты в московском времени
    const moscowDate = getMoscowDate(date);
    const year = moscowDate.getFullYear();
    const month = (moscowDate.getMonth() + 1).toString().padStart(2, '0');
    const day = moscowDate.getDate().toString().padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  };
  
  // Получение количества дней в месяце
  const getDaysInMonth = (year, month) => {
    return new Date(Date.UTC(year, month + 1, 0, 3, 0, 0)).getDate();
  };
  
  // Получение дня недели первого дня месяца (0 - Пн, 6 - Вс)
  const getFirstDayOfMonth = (year, month) => {
    const day = new Date(Date.UTC(year, month, 1, 3, 0, 0)).getDay();
    return day === 0 ? 6 : day - 1; // Преобразуем к формату, где 0 - Пн, 6 - Вс
  };
  
  // Построение матрицы дней месяца
  const buildCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    const daysInMonth = getDaysInMonth(year, month);
    const firstDayOfMonth = getFirstDayOfMonth(year, month);
    
    // Создаем пустой массив для всех дней в календаре (до 42 ячеек - 6 недель)
    const days = Array(42).fill(null);
    
    // Заполняем дни текущего месяца
    for (let i = 0; i < daysInMonth; i++) {
      days[i + firstDayOfMonth] = new Date(Date.UTC(year, month, i + 1, 3, 0, 0));
    }
    
    // Если последняя неделя пустая, удаляем её
    if (days[35] === null) {
      return days.slice(0, 35);
    }
    
    return days;
  };
  
  // Обработчик выбора даты
  const handleDateSelect = (date) => {
    if (!date) return;
    
    setSelectedDate(date);
    setShowCalendar(false);
    
    if (onChange) {
      // Передаем дату в формате московского времени
      onChange(date);
    }
  };
  
  // Переход к предыдущему месяцу
  const handlePrevMonth = () => {
    setCurrentMonth(prev => {
      const year = prev.getMonth() === 0 ? prev.getFullYear() - 1 : prev.getFullYear();
      const month = prev.getMonth() === 0 ? 11 : prev.getMonth() - 1;
      return new Date(Date.UTC(year, month, 1, 3, 0, 0));
    });
  };
  
  // Переход к следующему месяцу
  const handleNextMonth = () => {
    setCurrentMonth(prev => {
      const year = prev.getMonth() === 11 ? prev.getFullYear() + 1 : prev.getFullYear();
      const month = prev.getMonth() === 11 ? 0 : prev.getMonth() + 1;
      return new Date(Date.UTC(year, month, 1, 3, 0, 0));
    });
  };
  
  // Проверка, является ли дата текущей датой
  const isToday = (date) => {
    if (!date) return false;
    const today = getMoscowDate(new Date());
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };
  
  // Проверка, является ли дата выбранной датой
  const isSelected = (date) => {
    if (!date || !selectedDate) return false;
    return date.getDate() === selectedDate.getDate() &&
           date.getMonth() === selectedDate.getMonth() &&
           date.getFullYear() === selectedDate.getFullYear();
  };
  
  // Определение класса ячейки календаря
  const getCellClass = (date) => {
    if (!date) return 'calendar-day empty';
    
    let classes = 'calendar-day';
    
    if (isToday(date)) {
      classes += ' today';
    }
    
    if (isSelected(date)) {
      classes += ' selected';
    }
    
    return classes;
  };
  
  // Обработчик клика вне календаря
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Инициализация и обновление выбранной даты при изменении value извне
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const parsedDate = safeParseDate(value);
    
    // Используем функциональное обновление для setSelectedDate, чтобы не зависеть от selectedDate напрямую в effect
    setSelectedDate(currentSelected => {
    if (parsedDate) {
            // Обновляем текущий месяц только если месяц/год изменились
            if (!currentSelected || 
                parsedDate.getMonth() !== currentSelected.getMonth() || 
                parsedDate.getFullYear() !== currentSelected.getFullYear()) {
        setCurrentMonth(new Date(parsedDate.getFullYear(), parsedDate.getMonth(), 1));
      }
            return parsedDate; // Возвращаем новое значение для setSelectedDate
    } else {
            // Если не удалось распарсить, возвращаем текущую дату
      const today = new Date();
            const todayDateOnly = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate(), 3, 0, 0));
            setCurrentMonth(new Date(todayDateOnly.getFullYear(), todayDateOnly.getMonth(), 1));
            return todayDateOnly; // Возвращаем новое значение для setSelectedDate
    }
    });

  }, [value, safeParseDate]);
  
  return (
    <div className={`relative ${className}`}>
      {label && (
        <label className="block text-xs text-gray-600 mb-1">
          {label}
        </label>
      )}
      
      <div
        className="relative w-full"
        onClick={() => setShowCalendar(prev => !prev)}
      >
        <input
          type="text"
          readOnly
          value={selectedDate ? formatDate(selectedDate) : ''}
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-700 cursor-pointer"
          placeholder="Выберите дату"
        />
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
      </div>
      
      {showCalendar && (
        <div
          ref={calendarRef}
          className="custom-calendar"
        >
          {/* Заголовок календаря с навигацией */}
          <div className="calendar-header">
            <button
              type="button"
              onClick={handlePrevMonth}
              className="calendar-nav-button"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div className="calendar-month-year">
              {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </div>
            <button
              type="button"
              onClick={handleNextMonth}
              className="calendar-nav-button"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
          
          {/* Дни недели */}
          <div className="calendar-weekdays">
            {weekDays.map((day, index) => (
              <div
                key={index}
                className="calendar-weekday"
              >
                {day}
              </div>
            ))}
          </div>
          
          {/* Сетка календаря */}
          <div className="calendar-days">
            {buildCalendarDays().map((date, index) => (
              <div
                key={index}
                className={getCellClass(date)}
                onClick={() => date && handleDateSelect(date)}
              >
                {date ? date.getDate() : ''}
              </div>
            ))}
          </div>
          
          {/* Нижняя панель с кнопками */}
          <div className="calendar-footer">
            <button
              type="button"
              onClick={() => {
                const today = new Date();
                const todayWithoutTime = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                handleDateSelect(todayWithoutTime);
              }}
              className="calendar-button"
            >
              Сегодня
            </button>
            <button
              type="button"
              onClick={() => setShowCalendar(false)}
              className="calendar-button"
            >
              Закрыть
            </button>
          </div>
        </div>
      )}
      
      {/* Скрытое поле для совместимости с формами */}
      <input
        type="hidden"
        name={name}
        value={selectedDate ? formatISODate(selectedDate) : ''}
      />
    </div>
  );
};

export default CustomDatePicker; 